// App.jsx
// 📍 Location: frontend/App.jsx

import React from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";
import { Provider, useSelector } from "react-redux";

import { ConfigProvider } from "antd";
import { ToastContainer } from "react-toastify";

import { store } from "./app.store";
import { isOwnerAccessRestricted } from "./utils/subscriptionAccess";

// ✅ IMPORT RESTAURANT PROVIDER
import { RestaurantProvider } from "./context/RestaurantContext";

import Layout from "./layout";
import Login from "./pages/login";
import Home from "./pages/home";
import OpenCheck from "./pages/opencheck";
import OpenCheckCreate from "./pages/opencheck/openchecklist.create";
import OpenCheckUpdate from "./pages/opencheck/openchecklist.update";
import HotholdCheck from "./pages/hothold";
import HotholdCheckCreate from "./pages/hothold/hotholdchecklist.create";
import WeeklyCheck from "./pages/weekly";
import WeeklyCheckCreate from "./pages/weekly/weeklychecklist.create";
import WeeklyCheckUpdate from "./pages/weekly/weeklychecklist.update";

import MonthlyCheck from "./pages/monthly";  
import MonthlyCheckCreate from "./pages/monthly/monthlychecklist.create";  
import MonthlyCheckUpdate from "./pages/monthly/monthlychecklist.update"; 

import Delivery from "./pages/delivery";
import Restaurant from "./pages/restaurant";
import RestaurantCreatePage from "./pages/restaurant/restaurant.create";
import RestaurantEditPage from "./pages/restaurant/restaurant.edit";
import Compliance from "./pages/compliance";
import ComplianceCreatePage from "./pages/compliance/compliance.create";
import ComplianceEditPage from "./pages/compliance/compliance.edit";
//import Client from "./pages/client";
import CloseCheck from "./pages/closecheck";
import CloseCheckCreate from "./pages/closecheck/closechecklist.create";
import CloseCheckUpdate from "./pages/closecheck/closechecklist.update";
import Incident from "./pages/incident";
import IncidentCreate from "./pages/incident/incident.create";
import CleaningLogPage from "./pages/cleaning";
import CleaningLogCreate from "./pages/cleaning/cleaning.create";
import CleaningItemsPage from "./pages/cleaning/cleaning.items";
import Reports from "./pages/reports";
import AllergyCheck from "./pages/allergycheck";
import AllergyCreate from "./pages/allergycheck/allergy.create";
import AllergyEdit from "./pages/allergycheck/allergy.edit";
import AllergyEditNew from "./pages/allergycheck/allergy.edit.new";
import Team from "./pages/team";
import TeamView from "./pages/team/team.view";
import HotholdcheckUpdate from "./pages/hothold/hotholdchecklist.update";
import ComplianceList from "./pages/compliance/compliance.view";
import Settings from "./pages/settings";
import ComplianceChecklistCreate from "./pages/compliance/compliance.checklist.create";
import ComplianceChecklistView from "./pages/compliance/compliance.checklist.view";
import ComplianceChecklistEditPage from "./pages/compliance/compliance.checklist.edit";
import AdvertPage from "./pages/advert";
import AdvertCreatePage from "./pages/advert/advert.create";
import AdvertEditPage from "./pages/advert/advert.edit";
import SupplierPage from "./pages/supplier";
import RegisterPage from "./pages/register";
import AccountPage from "./pages/account";
import ForgotPasswordPage from "./pages/forgotPassword";
import EmailInputPage from "./pages/register/emailInput";
import MakePaymentPage from "./pages/register/makePayment";
import CreateRestaurantPage from "./pages/register/createRestaurant";
import { CreateAccountProvider } from "./provider/createAccountProvider";
import EmailVerifyPage from "./pages/register/emailVerify";
import CreatePasswordPage from "./pages/register/createPassword";
import AccountsPage from "./pages/accounts";
import ResetPasswordFormPage from "./pages/forgotPassword/resetPasswordForm";

import SetMemberPassword from "./pages/setmemberpassword";
import SetLocalAdminPassword from "./pages/setlocaladminpassword/SetLocalAdminPassword";

import Categories from "./pages/label/Categories";
import CategoryProducts from "./pages/label/CategoryProducts";
import FoodDateLabel from "./pages/label/FoodDateLabel";
import PrintQueue from "./pages/label/PrintQueue";
import AllergenManagement from "./pages/label/AllergenManagement";
import ProductDetails from "./pages/label/ProductDetails";
import ProductForm from "./pages/label/ProductForm";
import LocationCreate from "./pages/location/LocationCreate";
import LocationList from './pages/location/index';
import LocationEdit from './pages/location/LocationEdit';
import LocationSettings from './pages/location/LocationSettings';
import LocationDashboard from './pages/location/LocationDashboard';

import "./css/app.css";
import "./css/responsive.css";
import "./vendor/quill.snow.css";



const PrivateRoute = ({ children }) => {
  const isAuth = useSelector((state) => state.app.isAuth);
  const location = useLocation();

  return isAuth ? (
    children
  ) : (
    <Navigate
      to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
      replace
    />
  );
};

/** Inactive owners may only use My Account (resubscribe). */
const ActiveSubscriptionRoute = ({ children }) => {
  const user = useSelector((state) => state.app.user);
  const location = useLocation();
  const path = location.pathname.replace(/^\//, "").split("/")[0] || "";

  if (isOwnerAccessRestricted(user) && path !== "account") {
    return <Navigate to="/account" replace />;
  }

  return children;
};


const App = () => {
  return (
    <Provider store={store}>
      <ConfigProvider>
        <CreateAccountProvider>
          {/* ✅ WRAP WITH RestaurantProvider */}
          <RestaurantProvider>
            <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/create-account" element={<EmailInputPage />} />
                <Route path="/email-verifycation" element={<EmailVerifyPage />} />
                <Route path="/create-password" element={<CreatePasswordPage />} />
                <Route path="/make-payment" element={<MakePaymentPage />} />
                <Route path="location/create" element={<LocationCreate />} />

                <Route
                  path="/create-restaurant"
                  element={<CreateRestaurantPage />}
                />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                <Route 
                  path="/set-member-password" 
                  element={<SetMemberPassword />} 
                />
                
                <Route path="/user/set-password" element={<SetLocalAdminPassword />} />

                <Route
                  path="/reset-password"
                  element={<ResetPasswordFormPage />}
                />

                <Route
                  path="*"
                  element={
                    <PrivateRoute>
                      <ActiveSubscriptionRoute>
                      <Layout>
                        <Routes>
                          <Route path="" element={<Navigate to="/home" />} />
                          <Route path="home" element={<Home />} />
                          <Route path="provechecks" element={<HotholdCheck />} />
                          <Route
                            path="provechecks/create"
                            element={<HotholdCheckCreate />}
                          />
                          <Route
                            path="provechecks/edit/:id"
                            element={<HotholdcheckUpdate />}
                          />
                 <Route path="locations" element={<LocationList />} />
<Route path="locations/:id" element={<LocationDashboard />} />
<Route path="locations/edit/:id" element={<LocationEdit />} />
<Route path="locations/settings/:id" element={<LocationSettings />} />
                          <Route path="weeklychecks" element={<WeeklyCheck />} />
                          <Route
                            path="weeklychecks/create"
                            element={<WeeklyCheckCreate />}
                          />
                          <Route
                            path="weeklychecks/edit/:id"
                            element={<WeeklyCheckUpdate />}
                          />

                          <Route
                              path="monthlychecks"
                              element={<MonthlyCheck />}
                            />
                            <Route
                              path="monthlychecks/create"
                              element={<MonthlyCheckCreate />}
                            />
                            <Route
                              path="monthlychecks/edit/:id"
                              element={<MonthlyCheckUpdate />}
                            />

                          <Route path="delivery" element={<Delivery />} />
                          <Route path="supplier" element={<SupplierPage />} />
                          <Route path="openchecks" element={<OpenCheck />} />
                          <Route
                            path="openchecks/create"
                            element={<OpenCheckCreate />}
                          />
                          <Route
                            path="openchecks/edit/:id"
                            element={<OpenCheckUpdate />}
                          />
                          <Route path="restaurant" element={<Restaurant />} />
                          <Route
                            path="restaurant/create"
                            element={<RestaurantCreatePage />}
                          />
                          <Route
                            path="restaurant/:id"
                            element={<RestaurantEditPage />}
                          />

                          <Route path="compliance" element={<Compliance />} />
                          <Route
                            path="compliance/create/:type"
                            element={<ComplianceChecklistCreate />}
                          />
                          <Route
                            path="compliance/edit/:id"
                            element={<ComplianceChecklistEditPage />}
                          />
                          <Route
                            path="compliance_checks/checklist/:type"
                            element={<ComplianceChecklistView />}
                          />
                          <Route path="advert" element={<AdvertPage />} />
                          <Route
                            path="advert/create"
                            element={<AdvertCreatePage />}
                          />
                          <Route
                            path="advert/edit/:id"
                            element={<AdvertEditPage />}
                          />

                          <Route path="closechecks" element={<CloseCheck />} />
                          <Route
                            path="closechecks/create"
                            element={<CloseCheckCreate />}
                          />
                          <Route
                            path="closechecks/edit/:id"
                            element={<CloseCheckUpdate />}
                          />
                          <Route path="team" element={<Team />} />
                          <Route path="team/view/:id" element={<TeamView />} />
                          <Route path="incidents" element={<Incident />} />
                          <Route
                            path="incidents/create"
                            element={<IncidentCreate />}
                          />
                          <Route path="cleaning" element={<CleaningLogPage />} />
                          <Route
                            path="cleaning/create"
                            element={<CleaningLogCreate />}
                          />
                          <Route
                            path="cleaning/items"
                            element={<CleaningItemsPage />}
                          />
                          <Route path="reports" element={<Reports />} />
                          <Route path="allergy" element={<AllergyCheck />} />
                          <Route
                            path="allergy/create"
                            element={<AllergyCreate />}
                          />
                          <Route
                            path="allergy/edit/new"
                            element={<AllergyEditNew />}
                          />
                          <Route
                            path="allergy/edit/new/:id"
                            element={<AllergyEditNew />}
                          />
                          <Route
                            path="compliance_checks"
                            element={<ComplianceList />}
                          />
                          <Route path="account" element={<AccountPage />} />
                          <Route path="settings" element={<Settings />} />
                          <Route path="accounts" element={<AccountsPage />} />

                          <Route path="label/categories" element={<Categories />} />
                          <Route path="label/category/:id/products" element={<CategoryProducts />} />
                          <Route path="label/food-date-label" element={<FoodDateLabel />} />
                          <Route path="label/print-queue" element={<PrintQueue />} />
                          <Route path="label/allergens" element={<AllergenManagement />} />
                          <Route path="label/product/:id" element={<ProductDetails />} />
                          <Route path="label/product/:id/edit" element={<ProductForm />} />
                          <Route path="label/product/new" element={<ProductForm />} />
                        </Routes>
                      </Layout>
                      </ActiveSubscriptionRoute>
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Router>
          </RestaurantProvider>
        </CreateAccountProvider>
        <ToastContainer />
      </ConfigProvider>
    </Provider>
  );
};

export default App;